import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { FilterAltOff, Refresh } from '@mui/icons-material';

import {
  CustomAutoComplete,
  CustomTextField,
  HorizontalForm,
  InfoTooltip,
  renderTwoLineOption,
} from '../../../common/components';
import {
  CUSTOM_PERIOD,
  DESTINATION_SCOPE_OPTIONS,
  DATE_FIELDS,
  MATERIAL_TEXTS,
  PERIODS,
  VALUATION_BASIS_OPTIONS,
} from '../constants/materialDashboardConstants';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import { toProviderOptions } from '../../utils/toProviderOptions';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';
import { toMaterialGroupOptions } from '../../utils/toMaterialGroupOptions';
import { toArticleOptions } from '../../utils/toArticleOptions';
import { toProjectOptions } from '../../utils/toProjectOptions';
import { getProjectOptionLabel } from '../../internal-orders/utils/internalOrdersUtils';
import { MATERIAL_OPTION_TEXTS } from '../../constants/materialOptionTexts';
import { getFilterFieldSx } from '../../utils/getFilterFieldSx';

const toolbarSx = {
  px: 2.5,
  py: 1.5,
  gap: 1.25,
  alignItems: 'center',
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  overflowX: 'visible',
};

/**
 * Toolbar de filtros globales del tablero en una sola fila, con acciones como íconos.
 * @param {Object} props
 * @param {Object} props.filters Filtros vigentes.
 * @param {string} props.period Período elegido.
 * @param {Object} props.scopeOptions Viviendas y búsqueda de materiales.
 * @param {Array<Object>} props.warehouses Depósitos.
 * @param {Array<Object>} props.groups Grupos de materiales.
 * @param {Array<Object>} props.providers Proveedores.
 * @param {Array<string>} props.infoLines Alcance y cobertura de la valoración (tooltip).
 * @param {boolean} props.hasWarnings Si el backend informó advertencias.
 * @param {Function} props.onChange Recibe (nombre, valor).
 * @param {Function} props.onPeriodChange Recibe el período.
 * @param {Function} props.onClear Limpia filtros.
 * @param {Function} props.onRefresh Recalcula la valoración.
 * @param {boolean} props.isRefreshing Consulta en curso.
 * @returns {JSX.Element}
 */
const MaterialDashboardFilters = ({
  filters,
  period,
  scopeOptions,
  warehouses,
  groups,
  providers,
  infoLines,
  hasWarnings,
  onChange,
  onPeriodChange,
  onClear,
  onRefresh,
  isRefreshing,
}) => {
  const handleChange = (event) => onChange(event.target.name, event.target.value);

  return (
    <Box sx={{ backgroundColor: 'background.paper', borderBottom: '1px solid #ccc' }}>
      <HorizontalForm withBorder={false} sx={toolbarSx}>
        <CustomAutoComplete
          label={MATERIAL_TEXTS.destinationScope}
          name="destination_kind"
          value={filters.destination_kind || 'all'}
          options={DESTINATION_SCOPE_OPTIONS}
          onChange={handleChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.period}
          name="period"
          value={period}
          options={PERIODS}
          onChange={(event) => onPeriodChange(event.target.value)}
          sx={getFilterFieldSx()}
        />
        {period === CUSTOM_PERIOD && (
          <>
            <CustomTextField
              label={MATERIAL_TEXTS.dateFrom}
              type="date"
              name="date_from"
              value={filters.date_from || ''}
              onChange={handleChange}
              shrinkLabel
              sx={getFilterFieldSx()}
            />
            <CustomTextField
              label={MATERIAL_TEXTS.dateTo}
              type="date"
              name="date_to"
              value={filters.date_to || ''}
              onChange={handleChange}
              shrinkLabel
              sx={getFilterFieldSx()}
            />
          </>
        )}
        <CustomAutoComplete
          label={MATERIAL_TEXTS.dateField}
          name="date_field"
          value={filters.date_field}
          options={DATE_FIELDS}
          onChange={handleChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.pesosBasis}
          name="valuation_basis"
          value={filters.valuation_basis}
          options={VALUATION_BASIS_OPTIONS}
          onChange={handleChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.project}
          name="uuid_project"
          value={filters.uuid_project || ''}
          options={toProjectOptions(scopeOptions.projects, getProjectOptionLabel)}
          inputPlaceholder={MATERIAL_OPTION_TEXTS.searchProject}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={handleChange}
          sx={getFilterFieldSx(1.5, 170)}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.articleSearch}
          name="uuid_inventory_article"
          value={filters.uuid_inventory_article || ''}
          options={toArticleOptions(scopeOptions.articles)}
          renderOption={renderTwoLineOption}
          inputPlaceholder={MATERIAL_OPTION_TEXTS.searchArticle}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') scopeOptions.setSearch(value);
          }}
          loading={scopeOptions.loading}
          filterOptions={(options) => options}
          onChange={handleChange}
          sx={getFilterFieldSx(1.5, 170)}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.warehouse}
          name="uuid_warehouse"
          value={filters.uuid_warehouse || ''}
          options={toWarehouseOptions(warehouses)}
          renderOption={renderTwoLineOption}
          inputPlaceholder={MATERIAL_OPTION_TEXTS.searchWarehouse}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={handleChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.group}
          name="uuid_material_group"
          value={filters.uuid_material_group || ''}
          options={toMaterialGroupOptions(groups)}
          renderOption={renderTwoLineOption}
          inputPlaceholder={MATERIAL_OPTION_TEXTS.searchGroup}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={handleChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={MATERIAL_TEXTS.provider}
          name="uuid_provider_legal_entity"
          value={filters.uuid_provider_legal_entity || ''}
          options={toProviderOptions(providers)}
          renderOption={renderTwoLineOption}
          inputPlaceholder={MATERIAL_OPTION_TEXTS.searchProvider}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={handleChange}
          sx={getFilterFieldSx(1.5, 170)}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title={MATERIAL_TEXTS.refresh}>
            <span>
              <IconButton color="primary" onClick={onRefresh} disabled={isRefreshing}>
                {isRefreshing ? <CircularProgress size={20} /> : <Refresh />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={MATERIAL_TEXTS.clear}>
            <IconButton color="primary" onClick={onClear}>
              <FilterAltOff />
            </IconButton>
          </Tooltip>
          <InfoTooltip
            lines={infoLines}
            color={hasWarnings ? 'warning' : 'primary'}
            ariaLabel={MATERIAL_TEXTS.info}
          />
        </Box>
      </HorizontalForm>
    </Box>
  );
};

export default MaterialDashboardFilters;
